import { useState, useEffect } from "react";
import axios from "axios";
import { Download, FileText, Loader2, Package, CheckCircle2, ExternalLink, X } from "lucide-react";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

export const ProjectExport = ({ projectId, projectName }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [exported, setExported] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/projects/${projectId}/export-preview`, { withCredentials: true });
        setPreview(data && typeof data === 'object' ? data : null);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [projectId]);

  const handleExport = async (format) => {
    setExporting(format);
    setExported(null);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/export/${format}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(projectName || "export").replace(/\s+/g, "_").toLowerCase()}_${format}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setExported(format);
    } catch (e) { console.error(e); }
    finally { setExporting(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-foreground/45" /></div>;

  const formats = [
    {
      id: "canvas",
      name: "Canvas LMS",
      desc: "IMS Common Cartridge 1.3 compatible package for Instructure Canvas",
      icon: "C",
      color: "from-[#e63f24] to-[#c22a14]",
      features: ["IMS CC 1.3 manifest", "HTML content pages", "Task summary", "Metadata JSON"],
    },
    {
      id: "brightspace",
      name: "Brightspace (D2L)",
      desc: "IMS Content Package compatible with Desire2Learn Brightspace",
      icon: "B",
      color: "from-[#003a6b] to-[#005a9e]",
      features: ["IMS CP manifest", "HTML content pages", "Task summary", "Metadata JSON"],
    },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="export-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-foreground">LMS Export</h3>
          <p className="text-[11px] text-foreground/50 mt-0.5">Export project content to Learning Management Systems</p>
        </div>
        {preview && (
          <div className="flex items-center gap-4 text-[11px] text-foreground/40">
            <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{preview.artifacts?.length || 0} artifacts</span>
            <span className="flex items-center gap-1"><Package className="w-3 h-3" />{preview.task_count} tasks</span>
            <span className="flex items-center gap-1">{preview.stage_count} stages</span>
          </div>
        )}
      </div>

      {/* Export Format Cards */}
      <div className="grid grid-cols-2 gap-4">
        {formats?.map(fmt => (
          <div key={fmt.id} className="bg-white border border-border/50 rounded-2xl overflow-hidden" data-testid={`export-format-${fmt.id}`}>
            <div className={`bg-gradient-to-r ${fmt.color} px-5 py-4 flex items-center gap-3`}>
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white font-bold text-lg">{fmt.icon}</div>
              <div>
                <p className="text-sm font-bold text-white">{fmt.name}</p>
                <p className="text-[10px] text-white/70">{fmt.desc}</p>
              </div>
            </div>
            <div className="p-4">
              <p className="text-[10px] font-bold text-foreground/45 uppercase tracking-wider mb-2">Package includes</p>
              <div className="space-y-1.5 mb-4">
                {fmt.features?.map((f, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[11px] text-foreground/50">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />{f}
                  </div>
                ))}
              </div>
              <button
                onClick={() => handleExport(fmt.id)}
                disabled={exporting === fmt.id}
                className="w-full text-xs font-semibold px-4 py-2.5 rounded-xl bg-[#014d7d] text-white flex items-center justify-center gap-2 hover:bg-[#013d66] disabled:opacity-50 transition-colors"
                data-testid={`export-btn-${fmt.id}`}
              >
                {exporting === fmt.id ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</>
                ) : exported === fmt.id ? (
                  <><CheckCircle2 className="w-3 h-3" /> Downloaded!</>
                ) : (
                  <><Download className="w-3 h-3" /> Export as {fmt.name}</>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Artifact Preview */}
      {preview?.artifacts?.length > 0 && (
        <div className="bg-white border border-border/50 rounded-xl p-4">
          <h4 className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-3">Content to Export</h4>
          <div className="space-y-1.5">
            {preview.artifacts?.map((a, i) => (
              <div key={a.id || i} className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-foreground/[0.02]" data-testid={`export-artifact-${a.id}`}>
                <FileText className="w-3.5 h-3.5 text-[#014d7d]" />
                <span className="text-xs font-medium text-foreground flex-1 truncate">{a.title}</span>
                <span className="text-[10px] text-foreground/45 capitalize">{a.type}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {preview?.artifacts?.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
          <p className="text-xs text-amber-700 font-medium">No artifacts to export yet. Start conversations to generate content.</p>
        </div>
      )}
    </div>
  );
};
